import React, { useMemo, useState, useContext } from 'react';
import { Box, Typography, LinearProgress, Tooltip, Stack, Chip, Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import UserContext from '../../User/UserSettings/UserContext';

export interface StorageCapacityData {
    storageId: string;
    storageName: string;
    capacity: number | null;
}

interface CapacityMetricProps {
    storageData: StorageCapacityData[];
    onStorageSelect?: (storageId: string) => void;
}
// 
const CapacityMetric: React.FC<CapacityMetricProps> = ({ storageData, onStorageSelect }) => {
    const theme = useTheme();
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const { user } = useContext(UserContext) || { user: {} };

    // Calculate overall capacity (average of all storages)
    const overallCapacity = useMemo(() => {
        const validCapacities = storageData
            .map(s => s.capacity)
            .filter((c): c is number => c !== null && c !== undefined && !isNaN(c));
        
        if (validCapacities.length === 0) return null;
        
        const sum = validCapacities.reduce((acc, val) => acc + val, 0);
        return sum / validCapacities.length;
    }, [storageData]);

    // Sort storages for tooltip
    const sortedByCapacity = useMemo(() => {
        return [...storageData]
            .filter(s => s.capacity !== null && s.capacity !== undefined)
            .sort((a, b) => (b.capacity! - a.capacity!));
    }, [storageData]);

    // Tooltip Content
    const tooltipContent = (
        <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Capacity Breakdown
            </Typography>
            <Stack spacing={0.5} divider={<Box sx={{ height: '1px', bgcolor: 'divider', my: 0.5 }} />}>
                {sortedByCapacity.map((storage) => (
                    <Box 
                        key={storage.storageId} 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onStorageSelect) {
                                onStorageSelect(storage.storageId);
                                setTooltipOpen(false);
                            }
                        }}
                        sx={{
                            cursor: onStorageSelect ? 'pointer' : 'default',
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            transition: 'background-color 0.2s',
                            '&:hover': onStorageSelect ? {
                                bgcolor: alpha(theme.palette.primary.main, 0.08)
                            } : {}
                        }}
                    >
                        <Typography variant="body2" sx={{ mr: 2 }}>
                            {storage.storageName}
                        </Typography>
                        <Chip 
                            label={`${storage.capacity?.toFixed(0)}%`}
                            size="small"
                            sx={{ 
                                fontWeight: 600,
                                minWidth: 50
                            }}
                        />
                    </Box>
                ))}
                {sortedByCapacity.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No capacity data available
                    </Typography>
                )}
            </Stack>
        </Box>
    );

    return (
        <Tooltip 
            title={tooltipContent}
            arrow
            placement="bottom"
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            onOpen={() => !user?.mobile && setTooltipOpen(true)}
            disableFocusListener
            disableTouchListener
            disableHoverListener={user?.mobile}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                        color: 'text.primary',
                        border: 1,
                        borderColor: 'divider',
                        boxShadow: theme.shadows[8],
                        '& .MuiTooltip-arrow': {
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.common.white,
                            '&::before': {
                                border: 1,
                                borderColor: 'divider',
                            }
                        }
                    }
                }
            }}
        >
            <Paper 
                elevation={0}
                onClick={() => user?.mobile && setTooltipOpen((prev) => !prev)}
                sx={{ 
                    p: 1.5,
                    flex: 1,
                    border: 1, 
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    }
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <WarehouseIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Capacity
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.25}>
                        <Typography variant="h5" fontWeight="bold">
                            {overallCapacity !== null ? overallCapacity.toFixed(0) : '—'}
                        </Typography>
                        {overallCapacity !== null && (
                            <Typography variant="body2" color="text.secondary">
                                %
                            </Typography>
                        )}
                    </Box>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={overallCapacity ?? 0} 
                    sx={{
                        height: 6,
                        borderRadius: 1,
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 1,
                        }
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.7rem' }}>
                    {sortedByCapacity.length} storage{sortedByCapacity.length !== 1 ? 's' : ''}
                </Typography>
            </Paper>
        </Tooltip>
    );
};

export default CapacityMetric;
